import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card, Button, Spin, message, Avatar, Tag, Alert } from 'antd'
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  PhoneOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useAppSelector } from '../../../app/hooks'
import sensors from '../../../utils/sensors'
import html2canvas from 'html2canvas'

import PageHeader from '@/components/PageHeader'
import './CreditReport.scss'

type CreditData = {
  userName: string
  userAvatar: string
  telNum: string
  registerTime: string
  preferLocation: string
  creditScore: number
  creditRating: string
  reportTime: string
  activeDays: number
  highAbnormalCnt: number
  sumHighAbnormalCnt: number
  lowAbnormalCnt: number
  midAbnormalCnt: number
  lastSumHighAbnormalTime: string
  level: number
}

// const mockCreditData: CreditData = {
//   userName: '张三',
//   userAvatar: '',
//   telNum: '13800138000',
//   registerTime: '2023-01-15',
//   preferLocation: '北京市朝阳区',
//   creditScore: 85,
//   creditRating: '优秀',
//   reportTime: new Date().toLocaleString('zh-CN'),
//   activeDays: 365,
//   highAbnormalCnt: 0,
//   sumHighAbnormalCnt: 2,
//   lowAbnormalCnt: 3,
//   midAbnormalCnt: 1,
//   lastSumHighAbnormalTime: '2024-12-15 14:30:00',
//   level: 4
// }

// 等级映射关系
const levelMap: Record<number, { text: string; color: string }> = {
  1: { text: '初出茅庐', color: '#0093FF' }, // 暂不展示，但保留定义
  2: { text: '工作信用低', color: '#EA4D3D' },
  3: { text: '工作信用中等', color: '#F79D14' },
  4: { text: '工作信用高', color: '#47EA84' }
}

export default function CreditReport() {
  const { phone } = useParams<{ phone: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const group = useAppSelector((state) => state.group)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [data, setData] = useState<CreditData | null>(null)
  const [downloadingPDF, setDownloadingPDF] = useState(false)
  const reportMainRef = useRef<HTMLDivElement>(null)

  const trackEvent = (params: {
    actionType: string
    number1?: number
    photoNum?: string
  }) => {
    sensors.track('workgroup_b7847_completed', {
      requirement_id: 871,
      track_sign: 'aworkgroup.b7847.completed.workgroup_b7847_completed',
      number1: params.number1,
      actionType: params.actionType,
      photoNum: params.photoNum,
      role: group.myRole?.toString() || '',
      groupID: group.groupId?.toString() || ''
    })
  }

  useEffect(() => {
    trackEvent({
      actionType: '进入页面'
    })
  }, [])

  // 查询接口已完全移至首页，报告页只负责展示数据
  useEffect(() => {
    if (phone) {
      // 从路由 state 中获取数据
      const creditData = (location.state as any)?.creditData

      if (creditData) {
        // 有数据，直接展示
        setData(creditData)
        setLoading(false)
        setError(false)
      } else {
        // 没有数据（可能是直接访问 URL 或刷新页面），提示返回首页
        setError(true)
        setErrorMsg('请从首页输入手机号进行查询')
        setLoading(false)
      }
    }
  }, [phone, location.state])

  const handleBack = () => {
    trackEvent({
      actionType: '查询成功页点击返回',
      photoNum: phone
    })
    navigate('/credit')
  }

  const handleDownloadPDF = async () => {
    if (!data || !reportMainRef.current) return

    setDownloadingPDF(true)

    try {
      trackEvent({
        actionType: '查询成功页点击导出pdf',
        number1: data.creditScore,
        photoNum: phone
      })

      const element = reportMainRef.current

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#f5f7fa'
      })

      const imgData = canvas.toDataURL('image/png')

      const link = document.createElement('a')
      link.href = imgData
      link.download = `工作信用报告_${data.userName}_${phone}_${dayjs().format(
        'YYYYMMDD_HHmmss'
      )}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      message.success('报告已导出')
    } catch (error) {
      console.error('导出报告失败:', error)
      message.error('导出报告失败，请稍后重试')
    } finally {
      setDownloadingPDF(false)
    }
  }

  if (!phone) {
    return (
      <div className="credit-report-container">
        <Card className="credit-report-card">
          <div className="credit-report-error">
            <CloseCircleOutlined className="credit-report-error-icon" />
            <h3 className="credit-report-error-title">无效的查询</h3>
            <p className="credit-report-error-desc">
              请从首页输入手机号进行查询
            </p>
            <Button
              type="primary"
              onClick={handleBack}
              className="credit-report-error-btn"
            >
              <ArrowLeftOutlined />
              返回首页
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="credit-report-container">
        <Card className="credit-report-card">
          <div className="credit-report-loading">
            <Spin size="large" />
            <p className="credit-report-loading-text">正在查询信用信息...</p>
            <p className="credit-report-loading-subtext">请稍候</p>
          </div>
        </Card>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="credit-report-container">
        <Card className="credit-report-card">
          <div className="credit-report-error">
            <CloseCircleOutlined className="credit-report-error-icon" />
            <h3 className="credit-report-error-title">查询失败</h3>
            <p className="credit-report-error-desc">
              {errorMsg || '查询失败，请稍后重试'}
            </p>
            <Button
              type="primary"
              onClick={handleBack}
              className="credit-report-error-btn"
            >
              <ArrowLeftOutlined />
              返回首页
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // 获取等级信息
  const levelInfo = levelMap[data.level] || levelMap[4] // 默认使用等级4
  const levelText = levelInfo.text
  const levelColor = levelInfo.color

  return (
    <div className="credit-report-container">
      <PageHeader
        ghost={false}
        style={{
          borderBottom: '1px solid rgb(235, 237, 240)',
          overflow: 'visible'
        }}
        onBack={handleBack}
        title="返回"
        extra={[
          <Button
            key="download"
            type="primary"
            onClick={handleDownloadPDF}
            loading={downloadingPDF}
            disabled={downloadingPDF}
            style={{
              backgroundColor: '#EBF6FF',
              border: 'none',
              color: '#0070F4',
              fontWeight: 'bold'
            }}
          >
            <DownloadOutlined />
            {downloadingPDF ? '导出中...' : '导出报告'}
          </Button>
        ]}
      />

      <main className="credit-report-main" ref={reportMainRef}>
        <div className="credit-report-content">
          <Card className="credit-user-card">
            <div className="credit-user-info">
              <Avatar
                size={80}
                className="credit-user-avatar"
                src={data.userAvatar}
                onError={() => {
                  // 图片加载失败时，Avatar 组件会自动回退到 children
                  return true
                }}
              >
                {data.userName?.substring(0, 1) || '用'}
              </Avatar>
              <div className="credit-user-details">
                <h2 className="credit-user-name">{data.userName}</h2>
                <div className="credit-user-meta">
                  <span className="credit-user-meta-item">
                    <PhoneOutlined />
                    {data.telNum}
                  </span>
                  <span className="credit-user-meta-item">
                    <CalendarOutlined />
                    注册于 {data.registerTime}
                  </span>
                  {data.preferLocation &&
                    data.preferLocation !== '暂无数据' && (
                      <span className="credit-user-meta-item">
                        <EnvironmentOutlined />
                        {data.preferLocation}
                      </span>
                    )}
                </div>
              </div>
            </div>
          </Card>

          <Card className="credit-score-card">
            <div className="credit-score-header">
              <h3 className="credit-score-title">工作信用评分</h3>
              <p className="credit-score-subtitle">
                基于今日水印相机真实拍摄数据综合评估
              </p>
            </div>
            <div className="credit-score-content">
              <div className="credit-score-value">
                <span
                  className="credit-score-number"
                  style={{ color: levelColor }}
                >
                  {levelText}
                </span>
              </div>
              <p className="credit-score-time">
                报告生成时间：{data.reportTime}
              </p>
            </div>
          </Card>

          <div className="credit-stats-grid">
            <Card className="credit-stat-card credit-stat-blue">
              <div className="credit-stat-header">
                <CheckCircleOutlined />
                <span>真实拍照天数</span>
              </div>
              <div className="credit-stat-content">
                <p className="credit-stat-value">{data.activeDays}</p>
                <p className="credit-stat-desc">
                  累计活跃天数 · 约{(data.activeDays / 365).toFixed(1)}年
                </p>
              </div>
            </Card>

            <Card className="credit-stat-card credit-stat-orange">
              <div className="credit-stat-header">
                <ExclamationCircleOutlined />
                <span>重度异常次数</span>
              </div>
              <div className="credit-stat-content">
                <p className="credit-stat-value">{data.highAbnormalCnt}</p>
                <p className="credit-stat-desc">近一个月内</p>
              </div>
            </Card>

            <Card className="credit-stat-card credit-stat-red">
              <div className="credit-stat-header">
                <CloseCircleOutlined />
                <span>累计异常次数</span>
              </div>
              <div className="credit-stat-content">
                <p className="credit-stat-value">{data.sumHighAbnormalCnt}</p>
                <p className="credit-stat-desc">历史累计</p>
              </div>
            </Card>
          </div>

          <Card className="credit-abnormal-card">
            <div className="credit-abnormal-header">
              <h3 className="credit-abnormal-title">
                <ExclamationCircleOutlined />
                异常情况详情
              </h3>
              <p className="credit-abnormal-subtitle">照片验真异常统计</p>
            </div>
            <div className="credit-abnormal-content">
              <div className="credit-abnormal-item credit-abnormal-yellow">
                <div className="credit-abnormal-item-info">
                  <p className="credit-abnormal-item-title">轻度异常</p>
                  <p className="credit-abnormal-item-desc">近一个月内</p>
                </div>
                <p className="credit-abnormal-item-value">
                  {data.lowAbnormalCnt}
                </p>
              </div>
              <div className="credit-abnormal-item credit-abnormal-orange">
                <div className="credit-abnormal-item-info">
                  <p className="credit-abnormal-item-title">中度异常</p>
                  <p className="credit-abnormal-item-desc">近一个月内</p>
                </div>
                <p className="credit-abnormal-item-value">
                  {data.midAbnormalCnt}
                </p>
              </div>
              <div className="credit-abnormal-item credit-abnormal-red">
                <div className="credit-abnormal-item-info">
                  <p className="credit-abnormal-item-title">重度异常</p>
                  <p className="credit-abnormal-item-desc">近一个月内</p>
                </div>
                <p className="credit-abnormal-item-value">
                  {data.highAbnormalCnt}
                </p>
              </div>
              {data.lastSumHighAbnormalTime &&
                data.lastSumHighAbnormalTime !== '无' && (
                  <div className="credit-abnormal-time">
                    <ClockCircleOutlined />
                    <p className="credit-abnormal-time-text">
                      最近一次异常时间：
                      <span>{data.lastSumHighAbnormalTime}</span>
                    </p>
                  </div>
                )}
            </div>
          </Card>

          <Card className="credit-source-card">
            <div className="credit-source-header">
              <h3 className="credit-source-title">数据来源与技术认证</h3>
            </div>
            <div className="credit-source-content">
              <div className="credit-source-item">
                <CheckCircleOutlined />
                <p>
                  <span className="credit-source-label">
                    今日水印相机·真实时间认证：
                  </span>
                  所有数据基于今日水印相机真实拍摄记录，每张照片均带有中科院国家授时中心授时的精确时间戳
                </p>
              </div>
              <div className="credit-source-item">
                <CheckCircleOutlined />
                <p>
                  <span className="credit-source-label">5道检测程序验真：</span>
                  分辨率检测、数字防伪码、AI异常检测、时间戳验证、GPS定位，全方位确保照片真实性
                </p>
              </div>
              <div className="credit-source-item">
                <CheckCircleOutlined />
                <p>
                  <span className="credit-source-label">区块链存证技术：</span>
                  照片数据上链存证，防篡改防伪造，可作为法庭证据使用
                </p>
              </div>
            </div>
          </Card>
        </div>
      </main>

      <footer className="credit-report-footer">
        <div className="credit-report-footer-content">
          <p>今日水印相机 · 个人工作信用查询系统</p>
          <p className="credit-report-footer-subtitle">
            数据来源：今日水印相机真实拍摄平台 · 5道检测程序综合验真
          </p>
        </div>
      </footer>
    </div>
  )
}
